import {
  Box,
  Divider,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Grid,
  IconButton,
  Menu,
  MenuItem
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchMonthlyPayment, createCheckoutSession, fetchInvoices, setDefaultPaymentMethod, deletePaymentMethod } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";


const Billing = () => {
  const [searchParams] = useSearchParams();
  const [billingData, setBillingData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true); // page loading
  const [paying, setPaying] = useState(false); // PAY NOW loading
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [paymentCanceled, setPaymentCanceled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [processingPaymentMethod, setProcessingPaymentMethod] = useState(false);
  const { companyId, accountStatus } = useAuth();
  // Handle Stripe checkout redirect with ?status=completed or ?status=canceled
  useEffect(() => {
    const status = searchParams.get("status");
    const sessionId = searchParams.get("session_id");

    if (status === "completed" && sessionId) {
      const verifySession = async () => {
        try {
          // eslint-disable-next-line
          const sessionData = await verifyCheckoutSession(sessionId);
          // eslint-disable-next-line
          setPaymentSuccess(sessionData);
        } catch (err) {
          console.error(err);
          setError("Unable to verify payment. Please contact support if you were charged.");
        }
      };
      verifySession();
    } else if (status === "canceled") {
      setPaymentCanceled(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const loadBilling = async () => {
      try {
        setLoading(true);
        const data = await fetchMonthlyPayment(companyId);
        setBillingData(data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError("Failed to load billing details.");
      } finally {
        setLoading(false);
      }
    };

    loadBilling();
  }, [companyId]);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        const invoiceData = await fetchInvoices(companyId);
        setInvoices(invoiceData || []);
      } catch (err) {
        console.error("Error fetching invoices:", err);
      }
    };

    if (companyId) {
      loadInvoices();
    }
  }, [companyId]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (paymentSuccess) {
      const timer = setTimeout(() => {
        setPaymentSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentSuccess]);

  // Auto-clear error message after 7 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSessionCheckout = async (modeVar) => {
    try {
      setPaying(true);

      const requestSessionData = {
        companyId: companyId,
        mode: modeVar

      };

      const response = await createCheckoutSession(requestSessionData);
      console.log(response);
      const sessionUrl = response.data.data;

      if (!sessionUrl) {
        throw new Error("No checkout URL returned");
      }

      window.location.href = sessionUrl;
    } catch (err) {
      console.error(err);
      setError("Unable to start payment. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  const handleMenuOpen = (event, paymentMethod) => {
    setAnchorEl(event.currentTarget);
    setSelectedPaymentMethod(paymentMethod);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPaymentMethod(null);
  };

  const handleSetDefault = async () => {
    if (!selectedPaymentMethod) return;

    try {
      setProcessingPaymentMethod(true);
      await setDefaultPaymentMethod(companyId, selectedPaymentMethod.id);

      // Reload billing data to refresh payment methods
      const data = await fetchMonthlyPayment(companyId);
      setBillingData(data);
      setError(null);
      setPaymentSuccess({ message: "Default payment method updated successfully" });
    } catch (err) {
      console.error(err);
      setError("Failed to update default payment method. Please try again.");
    } finally {
      setProcessingPaymentMethod(false);
      handleMenuClose();
    }
  };

  const handleDeletePaymentMethod = async () => {
    if (!selectedPaymentMethod) return;

    try {
      setProcessingPaymentMethod(true);
      await deletePaymentMethod(companyId, selectedPaymentMethod.id);

      // Reload billing data to refresh payment methods
      const data = await fetchMonthlyPayment(companyId);
      setBillingData(data);
      setError(null);
      setPaymentSuccess({ message: "Payment method deleted successfully" });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to delete payment method. Please try again.");
    } finally {
      setProcessingPaymentMethod(false);
      handleMenuClose();
    }
  };

  const isExpired = accountStatus === 'EXPIRED';
  const isTrial = billingData?.subscriptionType === "TRIAL";
  const planName = billingData?.subscriptionType || "Standard";
  const activeCustomersCount = billingData?.activeCustomersCount ?? 0;
  const subscriptionPlanCharges = billingData?.subscriptionPlanCharges ?? null;
  const serviceChargePerCustomer = billingData?.serviceChargePerCustomer ?? null;
  const hasPricing = subscriptionPlanCharges !== null && serviceChargePerCustomer !== null;
  const monthlyAmount = hasPricing
    ? subscriptionPlanCharges + activeCustomersCount * serviceChargePerCustomer
    : null;

  const trialEndDate = billingData?.trialEndTs
    ? new Date(billingData.trialEndTs).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;
  const daysRemaining = billingData?.daysRemaining ?? null;

  const nextBillingDate = billingData?.nextBillingDate
    ? new Date(billingData.nextBillingDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <Box sx={{ p: 4, pt: '80px', backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Page Title */}
      <Typography variant="h4" fontWeight="bold" mb={4}>
        Membership & Billing
      </Typography>

      {/* Loading */}
      {loading && <CircularProgress />}

      {/* Payment Success */}
      {paymentSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPaymentSuccess(null)}>
          {paymentSuccess.message || "Payment completed successfully!"}
        </Alert>
      )}

      {/* Payment Canceled */}
      {paymentCanceled && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setPaymentCanceled(false)}>
          Payment successfully cancelled. No charges were made.
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Membership and Payment Methods Side by Side */}
          <Grid container spacing={3} mb={3}>
            {/* Membership Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6" fontWeight="bold">
                    Membership
                  </Typography>
                  {isExpired && <Chip label="Trial Ended" color="error" size="small" />}
                  {!isExpired && isTrial && <Chip label="Free Trial" color="warning" size="small" />}
                  {!isExpired && !isTrial && <Chip label="Active" color="success" size="small" />}
                </Box>

                {isExpired ? (
                  /* ── EXPIRED TRIAL STATE ── */
                  <Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography fontWeight="bold">Your free trial has ended</Typography>
                      {trialEndDate && (
                        <Typography variant="body2" mt={0.5}>
                          Trial ended on <b>{trialEndDate}</b>
                        </Typography>
                      )}
                    </Alert>

                    {nextBillingDate && (
                      <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 2, mb: 2 }}>
                        <Typography fontWeight="bold" color="primary.dark">
                          Your subscription is active
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          Next bill on <b>{nextBillingDate}</b>
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography color="text.secondary">Active customers</Typography>
                      <Typography fontWeight="bold">{activeCustomersCount}</Typography>
                    </Box>

                    {hasPricing && (
                      <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="text.secondary">Base subscription</Typography>
                          <Typography>${subscriptionPlanCharges?.toLocaleString()}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mt={1}>
                          <Typography color="text.secondary">
                            Per customer ({activeCustomersCount} × ${serviceChargePerCustomer})
                          </Typography>
                          <Typography>
                            ${(activeCustomersCount * serviceChargePerCustomer).toLocaleString()}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box display="flex" justifyContent="space-between">
                          <Typography fontWeight="bold">Estimated monthly charge</Typography>
                          <Typography fontWeight="bold">${monthlyAmount?.toLocaleString()}</Typography>
                        </Box>
                      </>
                    )}

                    {!nextBillingDate && (
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2, backgroundColor: '#002244', '&:hover': { backgroundColor: '#003366' } }}
                        onClick={() => handleSessionCheckout("subscription")}
                        disabled={paying}
                      >
                        {paying ? <CircularProgress size={20} color="inherit" /> : "Subscribe Now"}
                      </Button>
                    )}
                  </Box>
                ) : isTrial ? (
                  /* ── ACTIVE TRIAL STATE ── */
                  <Box>
                    <Typography color="text.secondary" mb={2}>
                      You are on a <b>15-day free trial</b>. No charges during this period.
                    </Typography>

                    {daysRemaining !== null && (
                      <Box sx={{ p: 2, backgroundColor: daysRemaining <= 7 ? "#fff3e0" : "#e8f5e9", borderRadius: 2, mb: 2 }}>
                        <Typography fontWeight="bold" color={daysRemaining <= 7 ? "warning.dark" : "success.dark"}>
                          {daysRemaining > 0 ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining` : "Trial expired"}
                        </Typography>
                        {trialEndDate && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            Trial ends on <b>{trialEndDate}</b>
                          </Typography>
                        )}
                      </Box>
                    )}

                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography color="text.secondary">Active customers</Typography>
                      <Typography fontWeight="bold">{activeCustomersCount}</Typography>
                    </Box>

                    <Alert severity="success" sx={{ mb: 2 }}>
                      Your subscription is set up. Billing starts automatically on{" "}
                      <b>{nextBillingDate ?? "your billing date"}</b> — no action needed.
                    </Alert>
                  </Box>
                ) : (
                  /* ── ACTIVE PAID PLAN STATE ── */
                  <Box>
                    <Typography mt={1}>
                      <b>{planName}</b>
                    </Typography>
                    <Typography color="text.secondary" mb={2}>
                      Billed monthly
                    </Typography>

                    <Box sx={{ mt: 2 }}>
                      <Typography fontWeight="bold" mb={1}>
                        Pricing Breakdown
                      </Typography>

                      <Box display="flex" justifyContent="space-between">
                        <Typography color="text.secondary">Base subscription</Typography>
                        <Typography>
                          {subscriptionPlanCharges !== null ? `$${subscriptionPlanCharges.toLocaleString()}` : "—"}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography color="text.secondary">
                          Active customers ({activeCustomersCount}
                          {serviceChargePerCustomer !== null ? ` × $${serviceChargePerCustomer}` : ""})
                        </Typography>
                        <Typography>
                          {serviceChargePerCustomer !== null
                            ? `$${(activeCustomersCount * serviceChargePerCustomer).toLocaleString()}`
                            : "—"}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold">Total monthly charge</Typography>
                        <Typography fontWeight="bold">
                          {monthlyAmount !== null ? `$${monthlyAmount.toLocaleString()}` : "—"}
                        </Typography>
                      </Box>
                    </Box>

                    {nextBillingDate && (
                      <Typography sx={{ mt: 2 }}>
                        Next payment: <b>{nextBillingDate}</b>
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Payment Methods Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight="bold">
                  Active Payment Methods
                </Typography>

                {billingData?.paymentMethods?.length > 0 ? (
                  billingData.paymentMethods.map((pm, index) => (
                    <Box
                      key={pm.id || index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <CreditCardIcon color="action" />
                        <Box>
                          <Typography fontWeight="bold">
                            {pm.card?.brand ? pm.card.brand.charAt(0).toUpperCase() + pm.card.brand.slice(1) : "Card"} •••• {pm.card?.last4 || "****"}
                          </Typography>
                          {pm.card?.expMonth && pm.card?.expYear && (
                            <Typography variant="body2" color="text.secondary">
                              Expires {String(pm.card.expMonth).padStart(2, "0")}/{pm.card.expYear}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        {pm.isDefault && (
                          <Chip label="Default" size="small" color="primary" variant="outlined" />
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, pm)}
                          disabled={processingPaymentMethod}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No payment methods on file.
                  </Typography>
                )}

                <Button variant="outlined" sx={{ mt: 2 }}
                  onClick={() => handleSessionCheckout("setup")}
                  disabled={paying}
                >
                  Add Payment Method
                </Button>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Billing History
            </Typography>

            {(isTrial && !isExpired) && invoices.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Invoices will appear here once your free trial ends and billing begins.
              </Typography>
            ) : invoices.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><b>Invoice</b></TableCell>
                      <TableCell><b>Date</b></TableCell>
                      <TableCell><b>Amount</b></TableCell>
                      <TableCell><b>Status</b></TableCell>
                      <TableCell><b>Actions</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.number || invoice.id}</TableCell>
                        <TableCell>
                          {invoice.created
                            ? new Date(invoice.created).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          ${(invoice.amountDue / 100).toFixed(2)} {invoice.currency?.toUpperCase()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.paid ? "Paid" : invoice.attempted ? "Failed" : "Pending"}
                            color={invoice.paid ? "success" : invoice.attempted ? "error" : "warning"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {invoice.hostedInvoiceUrl && (
                              <Link
                                href={invoice.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                View
                              </Link>
                            )}
                            {invoice.invoicePdf && (
                              <Link
                                href={invoice.invoicePdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                PDF
                              </Link>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Your previous invoices will appear here.
              </Typography>
            )}

          </Paper>
        </>
      )}

      {/* Payment Method Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedPaymentMethod && !selectedPaymentMethod.isDefault && (
          <MenuItem onClick={handleSetDefault}>
            Set as Default
          </MenuItem>
        )}
        <MenuItem
          onClick={handleDeletePaymentMethod}
          disabled={(billingData?.paymentMethods?.length ?? 0) <= 1}
          sx={{ color: (billingData?.paymentMethods?.length ?? 0) <= 1 ? 'text.disabled' : 'error.main' }}
        >
          {(billingData?.paymentMethods?.length ?? 0) <= 1 ? 'Delete (minimum 1 card required)' : 'Delete'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Billing;